import { createContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user on initial render
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await api.get('/api/auth/me');
        
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (err) {
        localStorage.removeItem('token');
        setError(err.response?.data?.msg || 'Authentication error');
      } finally {
        setLoading(false);
      }
    };
    
    loadUser();
  }, []);

  // Register user
  const register = async (formData) => {
    try {
      const res = await api.post('/api/auth/register', formData);
      
      if (res.data && res.data.token) {
        localStorage.setItem('token', res.data.token);
        
        // Make sure to load the user data
        await loadUser();
        
        // Verify authentication state was set correctly
        if (!isAuthenticated) {
          console.warn('User registered but authentication state was not set');
        }
        
        return { success: true };
      } else {
        console.error('Registration response missing token:', res.data);
        setError('Registration successful but login failed. Please try logging in.');
        return { 
          success: false, 
          error: 'Registration successful but login failed. Please try logging in.' 
        };
      }
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.msg || 
                          (err.response?.data?.errors ? err.response.data.errors[0]?.msg : 'Registration failed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login user with email and password
  const login = async (email, password) => {
    try {
      const res = await api.post('/api/auth/login', { email, password });
      
      localStorage.setItem('token', res.data.token);
      
      await loadUser();
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.msg || 'Invalid credentials');
      return { success: false, error: err.response?.data?.msg || 'Invalid credentials' };
    }
  };

  // Login with Google
  const loginWithGoogle = async (googleData) => {
    try {
      const res = await api.post('/api/auth/google', googleData);
      
      localStorage.setItem('token', res.data.token);
      
      await loadUser();
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.msg || 'Google login failed');
      return { success: false, error: err.response?.data?.msg || 'Google login failed' };
    }
  };

  // Send phone verification code
  const sendPhoneVerificationCode = async (phone, name) => {
    try {
      await api.post('/api/auth/phone/send-code', { phone, name });
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to send verification code');
      return { success: false, error: err.response?.data?.msg || 'Failed to send verification code' };
    }
  };

  // Verify phone number with code
  const verifyPhoneWithCode = async (phone, code) => {
    try {
      const res = await api.post('/api/auth/phone/verify', { phone, code });
      
      localStorage.setItem('token', res.data.token);
      
      await loadUser();
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.msg || 'Invalid verification code');
      return { success: false, error: err.response?.data?.msg || 'Invalid verification code' };
    }
  };

  // Load user data
  const loadUser = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return false;
    }
    
    try {
      const res = await api.get('/api/auth/me');
      
      if (res.data) {
        setUser(res.data);
        setIsAuthenticated(true);
        setLoading(false);
        return true;
      } else {
        throw new Error('No user data returned');
      }
    } catch (err) {
      console.error('Load user error:', err);
      localStorage.removeItem('token');
      // Token will be handled by the API utility
      setUser(null);
      setIsAuthenticated(false);
      setError(err.response?.data?.msg || 'Authentication error');
      setLoading(false);
      return false;
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        error,
        register,
        login,
        loginWithGoogle,
        sendPhoneVerificationCode,
        verifyPhoneWithCode,
        logout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
